import React from 'react';
import cx from 'classnames';
import { makeStyles } from 'tss-react/mui';

import { Grid, NoSSR, units } from '@hbf/dsl';
import { ListingPhoto } from '@ha/api/v2/types/Listing';

import { useIntl } from 'ha/i18n';
import { PhotoPlaceholder } from 'ha/components/PhotoPlaceholder';
import { ImageGrid } from 'ha/components/ImageGrid';
import { ImageGridSkeleton } from 'ha/components/ImageGrid/ImageGridSkeleton';
import { CELL_HEIGHT } from 'ha/components/ImageGrid/constants';
import { LoadableSwiperSlider } from 'ha/components/SwiperSlider';
import { Imgix } from 'ha/modules/Image/Imgix';
import { useMediaQuery } from 'ha/helpers/MediaQuery';

import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { useImgix } from 'ha/modules/Image/useImgix';
import { useCurrentScreenSize } from 'ha/modules/UI/helpers/CurrentScreenSize';

import NoImage from 'ha/custom-icons/no-image.svg';

import { ImageSlider } from './ImageSlider';
import { GridImgixSizeByScreenSize } from '../constants';
import { usePageRedesign } from '../hooks/usePageRedesign';

const useStyles = makeStyles()(theme => ({
  absolute: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  fullHeight: {
    height: '100%',
  },
  imageContainer: {
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    cursor: 'pointer',
  },
  imageContainerBorder: {
    borderRadius: 0,
    [theme.breakpoints.up('md')]: {
      borderRadius: units.rem(0.25),
    },
  },
  media: {
    position: 'relative',
    width: '100%',
    paddingTop: '66.66%' /* 3:2 Aspect Ratio */,
    zIndex: 9,
    [theme.breakpoints.up('md')]: {
      paddingTop: '50%' /* 2:1 Aspect Ratio */,
    },
  },
  photoPlaceholder: {
    width: '100vw',
    height: `${CELL_HEIGHT.Mobile * 2}px`,
    [theme.breakpoints.up('md')]: {
      height: `${CELL_HEIGHT.Desktop * 2}px`,
      width: 'inherit',
    },
  },
  imageCover: {
    objectFit: 'cover',
    height: '100%',
    width: '100%',
  },
}));

export interface Props {
  imageGridProps: {
    tiles: ListingPhoto[];
    onImageClick: (id: number) => void;
  };
}

export const GalleryGridSection: React.FC<Props> = ({ imageGridProps }) => {
  const { classes } = useStyles();
  const { T } = useIntl();
  const { md } = useMediaQuery();
  const { transformImageURL } = useImgix();

  const currentScreenSize = useCurrentScreenSize();
  const withPageRedesign = usePageRedesign();

  const { showNewGallery } = useFeatureFlags();
  const isNewGallery = showNewGallery === 'on';

  const { tiles, onImageClick } = imageGridProps;

  const transformedTiles = React.useMemo(
    () =>
      tiles.map(image => ({
        ...image,
        url: transformImageURL(image.url, {
          ...GridImgixSizeByScreenSize[currentScreenSize],
          fit: 'crop',
        }),
      })),
    [currentScreenSize, tiles, transformImageURL],
  );

  const items = React.useMemo(
    () =>
      tiles?.map(({ url }, index) => ({
        key: url,
        element: (
          <div
            aria-hidden="true"
            className={cx(classes.imageContainer, classes.imageContainerBorder)}
            onClick={() => onImageClick(index)}
          >
            <Imgix
              className={classes.imageCover}
              htmlAttributes={{ role: 'presentation', loading: 'lazy' }}
              imgixParams={{ cs: 'srgb' }}
              sizes="100vw"
              src={url}
            />
          </div>
        ),
      })),
    [tiles, classes, onImageClick],
  );

  return (
    <React.Fragment>
      {tiles.length > 0 ? (
        <NoSSR
          fallback={<ImageGridSkeleton numPhotos={md ? tiles.length : 1} />}
        >
          {md && !withPageRedesign ? (
            <ImageGrid {...imageGridProps} tiles={transformedTiles} />
          ) : (
            <React.Fragment>
              {isNewGallery ? (
                <div
                  data-test-locator="Listing/GalleryGridSection/SwiperSlide"
                  className={classes.media}
                >
                  <div className={cx(classes.absolute, classes.fullHeight)}>
                    <LoadableSwiperSlider
                      items={items}
                      withKeyboardControls
                      withDefaultControls
                    />
                  </div>
                </div>
              ) : (
                <ImageSlider photos={transformedTiles} onClick={onImageClick} />
              )}
            </React.Fragment>
          )}
        </NoSSR>
      ) : (
        <Grid
          item
          className={classes.photoPlaceholder}
          data-test-locator="Grid section No media"
        >
          <PhotoPlaceholder IconComponent={NoImage} text={T('No images')} />
        </Grid>
      )}
    </React.Fragment>
  );
};
